<script lang="ts" setup>
  import { AutoSetIcon, BreakStartIcon, BreakStopIcon, DeleteIcon, NoteIcon, OfficeIcon, PlusIcon, ResetIcon, SendIcon, WorkStopIcon } from '@/config/icons'

  import Dialog from 'primevue/dialog'
  import FloatLabel from 'primevue/floatlabel'
  import Message from 'primevue/message'
  import { useToast } from 'primevue/usetoast'

  import { array, object, string } from 'yup'
  import { DateTime } from 'luxon'
  import { useFieldArray, useForm } from 'vee-validate'
  import { type Component, computed, toRefs } from 'vue'
  import { useTitrop } from '@/composables/useTitrop.ts'
  import type { WorktimeTrackingDayJsonldDefaultRead, WorktimeTrackingDayVersionEntry, WorktimeTrackingDayVersionEntryJsonldDefaultRead } from '@/api'
  import { useGeneratedWorktimeTrackingDayVersionStore } from '@/stores'
  import TimePicker from '@/elements/TimePicker.vue'
  import ButtonDefault from '@/elements/ButtonDefault.vue'

  const toastService = useToast()

  export interface EditDayFormProps {
    dayToEdit: WorktimeTrackingDayJsonldDefaultRead
  }

  type EntryAction = 'WORK_BEGIN' | 'BREAK_BEGIN' | 'BREAK_END' | 'WORK_END'

  interface EditEntryObject {
    action: EntryAction
    timestamp: string | undefined
    originalTimestamp: string | undefined
    id: string | undefined
    note: string | undefined | null
    draft: boolean | undefined
  }

  interface ActionConfig {
    label: string
    icon: Component
  }

  const actionConfig: Record<EntryAction, ActionConfig> = {
    WORK_BEGIN: { label: 'Arbeitsbeginn', icon: OfficeIcon },
    BREAK_BEGIN: { label: 'Pausenbeginn', icon: BreakStartIcon },
    BREAK_END: { label: 'Pausenende', icon: BreakStopIcon },
    WORK_END: { label: 'Arbeitsende', icon: WorkStopIcon }
  }

  const props = defineProps<EditDayFormProps>()
  const { dayToEdit } = toRefs(props)

  const isVisible = defineModel<boolean>('isVisible', { default: false })
  const isToday = computed(() => DateTime.now().startOf('day').toISO({ suppressMilliseconds: true }) === dayToEdit.value.date)

  const emit = defineEmits(['submit'])

  const titrop = useTitrop()
  const wttVersionStore = useGeneratedWorktimeTrackingDayVersionStore()

  const title = computed(() => 'Tag bearbeiten: ' + DateTime.fromISO(dayToEdit.value.date ?? '').toFormat('dd.LL.yyyy'))

  const emptyEntry = (action: EntryAction): EditEntryObject => ({
    action,
    timestamp: undefined,
    originalTimestamp: undefined,
    id: undefined,
    note: undefined,
    draft: undefined
  })

  const formEditEntries = (entries: WorktimeTrackingDayVersionEntryJsonldDefaultRead[]) => {
    const sortedEntries = entries
      .filter((entry) => entry.action && actionConfig[entry.action as EntryAction])
      .map<EditEntryObject>((entry) => ({
        action: entry.action as EntryAction,
        timestamp: entry.timestamp ?? undefined,
        originalTimestamp: entry.timestamp ?? undefined,
        id: entry['@id'],
        note: entry.note,
        draft: entry.draft
      }))
      .sort((a, b) => DateTime.fromISO(a.timestamp ?? '').toMillis() - DateTime.fromISO(b.timestamp ?? '').toMillis())

    const result = sortedEntries.flatMap<EditEntryObject>((entry, index) => {
      if (entry.action === 'BREAK_BEGIN' && sortedEntries[index + 1]?.action !== 'BREAK_END') return [entry, emptyEntry('BREAK_END')]
      return [entry]
    })

    if (result.at(0)?.action !== 'WORK_BEGIN') result.unshift(emptyEntry('WORK_BEGIN'))
    if (result.at(-1)?.action !== 'WORK_END') result.push(emptyEntry('WORK_END'))

    return result
  }

  const initialEntries = dayToEdit.value.activeVersion?.entries?.length ? formEditEntries(dayToEdit.value.activeVersion.entries) : [emptyEntry('WORK_BEGIN'), emptyEntry('WORK_END')]

  const timestampSchema = object().shape({
    action: string().oneOf(Object.keys(actionConfig)).required(),
    timestamp: string()
      .test('isRequired', 'Dieses Feld ist erforderlich', (value, context) => {
        return Boolean(value) || isTimestampOptional(context.parent.action)
      })
      .test('isNotLaterAsNow', 'Buchungen in der Zukunft sind nicht erlaubt', (value) => {
        if (!value || !isToday.value) return true
        return (
          value <=
          DateTime.now().toISO({
            includeOffset: false,
            suppressMilliseconds: true
          })
        )
      })
  })

  const mainSchema = object().shape({
    timestamps: array()
      .of(timestampSchema)
      .required()
      .test('isStructureValid', 'Ungültige Reihenfolge oder Zeitangabe', () => checkStructure(formEntries()))
  })

  const { handleSubmit, errors, meta, submitCount, resetForm } = useForm({
    validationSchema: mainSchema,
    initialValues: {
      timestamps: initialEntries
    }
  })

  const timestampArray = useFieldArray<EditEntryObject>('timestamps')
  const fields = timestampArray.fields

  const isTimestampOptional = (action: EntryAction | undefined) => {
    return isToday.value && (action === 'BREAK_END' || action === 'WORK_END')
  }

  const canAddPause = computed(() => fields.value.slice(0, -1).every((entry) => Boolean(entry.value.timestamp)))
  const canAddWorkInterval = computed(() => fields.value.every((entry) => Boolean(entry.value.timestamp)))

  const addPause = () => {
    if (!canAddPause.value) return
    const workEndIndex = fields.value.length - 1
    timestampArray.insert(workEndIndex, emptyEntry('BREAK_BEGIN'))
    timestampArray.insert(workEndIndex + 1, emptyEntry('BREAK_END'))
  }

  const addWorkInterval = () => {
    if (!canAddWorkInterval.value) return
    timestampArray.push(emptyEntry('WORK_BEGIN'))
    timestampArray.push(emptyEntry('WORK_END'))
  }

  const canRemovePause = (index: number) => {
    const action = fields.value[index]?.value.action
    if (action === 'BREAK_END') return true
    return action === 'BREAK_BEGIN' && fields.value[index + 1]?.value.action !== 'BREAK_END'
  }

  const removePause = (index: number) => {
    if (!canRemovePause(index)) return
    if (fields.value[index].value.action === 'BREAK_END' && fields.value[index - 1]?.value.action === 'BREAK_BEGIN') {
      timestampArray.remove(index)
      timestampArray.remove(index - 1)
      return
    }
    timestampArray.remove(index)
  }

  const getWorkIntervalStart = (workEndIndex: number) => {
    if (fields.value[workEndIndex]?.value.action !== 'WORK_END') return undefined

    for (let index = workEndIndex - 1; index >= 0; index--) {
      const action = fields.value[index].value.action
      if (action === 'WORK_BEGIN') return index
      if (action === 'WORK_END') return undefined
    }

    return undefined
  }

  const canRemoveWorkInterval = (index: number) => {
    const workStartIndex = getWorkIntervalStart(index)
    return workStartIndex !== undefined && workStartIndex > 0
  }

  const removeWorkInterval = (workEndIndex: number) => {
    const workStartIndex = getWorkIntervalStart(workEndIndex)
    if (workStartIndex === undefined || workStartIndex === 0) return

    for (let index = workEndIndex; index >= workStartIndex; index--) timestampArray.remove(index)
  }

  const formatDuration = (milliseconds: number) => {
    const totalMinutes = Math.max(0, Math.floor(milliseconds / 60_000))
    return `${Math.floor(totalMinutes / 60)}:${String(totalMinutes % 60).padStart(2, '0')}`
  }

  const timeSummary = computed(() => {
    let totalWork = 0
    let totalBreak = 0
    let workStart: number | undefined
    let breakStart: number | undefined

    fields.value.forEach(({ value: entry }) => {
      if (!entry.timestamp) return
      const timestamp = DateTime.fromISO(entry.timestamp).toMillis()
      if (!Number.isFinite(timestamp)) return

      switch (entry.action) {
        case 'WORK_BEGIN':
          workStart = timestamp
          break
        case 'BREAK_BEGIN':
          breakStart = timestamp
          break
        case 'BREAK_END':
          if (breakStart !== undefined) totalBreak += Math.max(0, timestamp - breakStart)
          breakStart = undefined
          break
        case 'WORK_END':
          if (workStart !== undefined) totalWork += Math.max(0, timestamp - workStart)
          workStart = undefined
          break
      }
    })

    if (isToday.value) {
      const now = DateTime.now().toMillis()
      if (workStart !== undefined) totalWork += Math.max(0, now - workStart)
      if (breakStart !== undefined) totalBreak += Math.max(0, now - breakStart)
    }

    return {
      work: formatDuration(totalWork - totalBreak),
      break: formatDuration(totalBreak)
    }
  })

  const isOriginalEntry = (entry: EditEntryObject) => {
    return Boolean(entry.id && entry.timestamp === entry.originalTimestamp)
  }

  const formEntries = (): Partial<WorktimeTrackingDayVersionEntry>[] => {
    return fields.value.flatMap<Partial<WorktimeTrackingDayVersionEntry>>((field) => {
      const entry = field.value
      if (!entry.timestamp) return []

      if (isOriginalEntry(entry)) {
        const originalEntry = dayToEdit.value.activeVersion?.entries?.find((candidate) => candidate['@id'] === entry.id)
        if (originalEntry) return [originalEntry]
      }

      return [
        {
          action: entry.action,
          timestamp: entry.timestamp,
          draft: false
        }
      ]
    })
  }

  function checkStructure(actions: Partial<WorktimeTrackingDayVersionEntry>[]) {
    if (!actions.length) return false

    let state: 'idle' | 'working' | 'break' = 'idle'
    let previousTimestamp: string | undefined
    let hasStarted = false

    for (const action of actions) {
      if (!action.timestamp || (previousTimestamp && action.timestamp <= previousTimestamp)) return false

      switch (action.action) {
        case 'WORK_BEGIN':
          if (state !== 'idle') return false
          state = 'working'
          hasStarted = true
          break
        case 'BREAK_BEGIN':
          if (state !== 'working') return false
          state = 'break'
          break
        case 'BREAK_END':
          if (state !== 'break') return false
          state = 'working'
          break
        case 'WORK_END':
          if (state !== 'working') return false
          state = 'idle'
          break
        default:
          return false
      }

      previousTimestamp = action.timestamp
    }

    return hasStarted && (isToday.value || state === 'idle')
  }

  const onSubmit = handleSubmit(async () => {
    const entriesArray = formEntries()
    await wttVersionStore.post({
      body: {
        creator: titrop.value.user.currentIri,
        entries: entriesArray,
        day: dayToEdit.value['@id'],
        decision: titrop.value.user.roles.management || titrop.value.user.roles.worktime_selfedit ? 'APPROVED' : undefined
      }
    })
    if (titrop.value.user.roles.management || titrop.value.user.roles.worktime_selfedit)
      toastService.add({
        severity: 'success',
        life: 2500,
        summary: 'Gespeichert'
      })
    else toastService.add({ severity: 'success', life: 2500, summary: 'Die Anfrage wurde gesendet' })
    emit('submit')
    isVisible.value = false
  })

  const isSystemMessage = (note: string) => {
    return note === 'Wurde automatisch generiert. Grund: Statusänderung.'
  }
</script>

<template>
  <Dialog
    v-model:visible="isVisible"
    :header="title"
    :style="{ width: '36rem' }"
    @keydown.capture.ctrl.enter.prevent="onSubmit"
    modal>
    <form @submit.prevent="onSubmit">
      <ol
        class="m-0 flex list-none flex-col gap-3 p-0"
        aria-label="Zeitverlauf">
        <template
          v-for="(entry, index) in fields"
          :key="entry.key">
          <li
            v-if="entry.value.action === 'WORK_END'"
            class="grid min-h-10 grid-cols-[1.25rem_minmax(0,1fr)_2rem] gap-3">
            <span
              class="relative after:absolute after:top-0 after:bottom-[-1.875rem] after:left-[0.5625rem] after:border-l-2 after:border-dashed after:border-[var(--p-content-border-color)] after:content-['']"
              aria-hidden="true" />
            <ButtonDefault
              :disabled="!canAddPause"
              :icon="PlusIcon"
              label="Pause hinzufügen"
              class="!pl-0 justify-self-start self-start"
              severity="secondary"
              size="small"
              type="button"
              variant="text"
              @click="addPause" />
            <span aria-hidden="true" />
          </li>

          <li
            :class="[
              'grid min-h-14 grid-cols-[1.25rem_minmax(0,1fr)_2rem] gap-3',
              index === fields.length - 2
                ? '[&_.worktime-marker]:after:w-0 [&_.worktime-marker]:after:border-l-2 [&_.worktime-marker]:after:border-dashed [&_.worktime-marker]:after:border-[var(--p-content-border-color)] [&_.worktime-marker]:after:bg-transparent'
                : '',
              entry.value.action === 'WORK_END' && index === fields.length - 1 ? '[&_.worktime-marker]:after:hidden' : ''
            ]">
            <span
              class="worktime-marker relative self-stretch after:absolute after:top-6 after:bottom-[-1.875rem] after:left-[0.5625rem] after:w-0.5 after:bg-[var(--p-content-border-color)] after:content-['']"
              aria-hidden="true">
              <span class="absolute top-[1.125rem] left-[0.3125rem] z-[1] size-2.5 rounded-full border-2 border-[var(--p-content-background)] bg-[var(--p-primary-color)] shadow-[0_0_0_1px_var(--p-primary-color)]" />
            </span>

            <div class="grid grid-cols-[minmax(8.5rem,0.8fr)_minmax(11rem,1.2fr)] items-center gap-3 max-sm:grid-cols-[minmax(0,1fr)] max-sm:gap-2">
              <div class="hidden md:flex min-w-0 items-center gap-2">
                <component
                  :is="actionConfig[entry.value.action].icon"
                  class="size-4.5 shrink-0 text-[var(--p-text-muted-color)]"
                  aria-hidden="true" />
                <span class="font-semibold">{{ actionConfig[entry.value.action].label }}</span>
              </div>

              <div class="min-w-0">
                <div class="flex min-w-0 items-center gap-1">
                  <FloatLabel
                    class="min-w-0 grow"
                    variant="on">
                    <label class="hidden md:block">Zeit:</label>
                    <label class="flex md:hidden min-w-0 items-center gap-2">
                      <component
                        :is="actionConfig[entry.value.action].icon"
                        class="size-4.5 shrink-0 text-[var(--p-text-muted-color)]"
                        aria-hidden="true" />
                      <span class="font-semibold">{{ actionConfig[entry.value.action].label }}</span>
                    </label>
                    <TimePicker
                      v-model="entry.value.timestamp"
                      :base-date="dayToEdit.date"
                      :class="{ invalid: errors[`timestamps[${index}].timestamp`] && submitCount }" />
                  </FloatLabel>

                  <AutoSetIcon
                    v-if="entry.value.draft && isOriginalEntry(entry.value)"
                    v-tooltip="{ value: 'Wurde automatisch generiert. Grund: fehlender Eintrag.', modifiers: { top: true } }"
                    class="size-7 shrink-0" />
                  <component
                    :is="isSystemMessage(entry.value.note ?? '') ? AutoSetIcon : NoteIcon"
                    v-if="entry.value.note && isOriginalEntry(entry.value)"
                    v-tooltip="{ value: entry.value.note, modifiers: { top: true } }"
                    class="size-7 shrink-0" />
                </div>
                <small
                  v-if="errors[`timestamps[${index}].timestamp`] && submitCount"
                  class="mt-1 block text-[var(--p-form-field-invalid-border-color)]"
                  role="alert">
                  {{ errors[`timestamps[${index}].timestamp`] }}
                </small>
              </div>
            </div>

            <ButtonDefault
              v-if="canRemovePause(index)"
              v-tooltip="{ value: 'Pause entfernen', modifiers: { top: true } }"
              :icon="DeleteIcon"
              aria-label="Pause entfernen"
              class="size-8 self-center"
              outlined
              severity="danger"
              size="extra-small"
              type="button"
              @click="removePause(index)" />
            <ButtonDefault
              v-else-if="canRemoveWorkInterval(index)"
              v-tooltip="{ value: 'Arbeitszeit entfernen', modifiers: { top: true } }"
              :icon="DeleteIcon"
              aria-label="Arbeitszeit entfernen"
              class="size-8 self-center"
              outlined
              severity="danger"
              size="extra-small"
              type="button"
              @click="removeWorkInterval(index)" />
            <span
              v-else
              aria-hidden="true" />
          </li>

          <li
            v-if="entry.value.action === 'WORK_END' && index === fields.length - 1"
            class="grid min-h-10 grid-cols-[1.25rem_minmax(0,1fr)_2rem] gap-3">
            <span
              class="relative after:hidden"
              aria-hidden="true" />
            <ButtonDefault
              :disabled="!canAddWorkInterval"
              :icon="PlusIcon"
              label="Arbeitszeit hinzufügen"
              class="!pl-0 justify-self-start self-start"
              severity="secondary"
              size="small"
              type="button"
              variant="text"
              @click="addWorkInterval" />
            <span aria-hidden="true" />
          </li>
        </template>
      </ol>

      <div
        v-if="submitCount && errors['timestamps']"
        class="mt-4">
        <Message class="w-full">
          {{ errors['timestamps'] }}
        </Message>
      </div>
    </form>

    <template #footer>
      <div class="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 max-sm:grid-cols-2">
        <ButtonDefault
          :icon="ResetIcon"
          :disabled="!meta.dirty"
          label="Zurücksetzen"
          size="small"
          outlined
          @click="resetForm" />
        <div
          class="flex flex-wrap justify-center gap-x-4 gap-y-1 text-sm text-[var(--p-text-muted-color)] [&_strong]:text-[var(--p-text-color)] max-sm:col-span-2 max-sm:row-start-1"
          aria-live="polite">
          <span>
            Arbeitszeit:
            <strong>{{ timeSummary.work }}</strong>
          </span>
          <span>
            Pause:
            <strong>{{ timeSummary.break }}</strong>
          </span>
        </div>
        <ButtonDefault
          :icon="SendIcon"
          label="Absenden"
          size="small"
          @click="onSubmit" />
      </div>
    </template>
  </Dialog>
</template>
